import { Steps } from 'antd';
import { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { type ModuleClass, type CompanySpecs } from './getAvailableModules';
import ModuleClassConfiguration from './ModuleClassConfiguration';
import ModuleClassSelection from './ModuleClassSelection';
import ModuleCompanySelection from './ModuleCompanySelection';

type State =
    | { step: 'companySelection' }
    | { step: 'classSelection'; company: CompanySpecs }
    | { step: 'settings'; selectedClass: ModuleClass; company: CompanySpecs };

type Action =
    | { type: 'goBackToCompanySelection' }
    | { type: 'goBackToClassSelection' }
    | { type: 'goToClassSelection'; company: CompanySpecs }
    | { type: 'goToSettings'; selectedClass: ModuleClass };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'goBackToCompanySelection':
            return { step: 'companySelection' };

        case 'goBackToClassSelection':
            if (state.step === 'companySelection') {
                throw new Error('Cannot invoke action "companySelection" with the current state');
            }

            return { step: 'classSelection', company: state.company };

        case 'goToClassSelection':
            return { step: 'classSelection', company: action.company };

        case 'goToSettings':
            if (state.step !== 'classSelection') {
                throw new Error('Cannot invoke action "goToSettings" with the current state');
            }

            return { step: 'settings', selectedClass: action.selectedClass, company: state.company };

        default:
            return state;
    }
};

const stepIndexes: State['step'][] = ['companySelection', 'classSelection', 'settings'];

const AddModulePage = () => {
    const { t } = useTranslation(['moduleDetails']);
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, { step: 'companySelection' });

    const actions = useMemo(
        () => ({
            goBackToCompanySelection: () => dispatch({ type: 'goBackToCompanySelection' }),
            goBackToClassSelection: () => dispatch({ type: 'goBackToClassSelection' }),
            goToClassSelection: (company: CompanySpecs) => dispatch({ type: 'goToClassSelection', company }),
            goToSettings: (moduleClass: ModuleClass) => dispatch({ type: 'goToSettings', selectedClass: moduleClass }),
        }),
        [dispatch]
    );

    const current = stepIndexes.findIndex(step => step === state.step);

    const stateDescription = useMemo(() => {
        switch (state.step) {
            case 'classSelection':
                return { company: state.company?.displayName || '', className: '' };

            case 'settings':
                return { company: state.company?.displayName || '', className: state.selectedClass?.label || '' };

            default:
                return { company: '', className: '' };
        }
    }, [state]);

    const content = useMemo(
        () => (
            <div style={{ maxWidth: '960px', margin: 'auto' }}>
                <Steps current={current} type="default">
                    <Steps.Step
                        description={stateDescription.company}
                        status={state.step === 'companySelection' ? 'process' : 'finish'}
                        title={t('moduleDetails:addModule.companySelection.title')}
                    />
                    <Steps.Step
                        description={stateDescription.className}
                        status={state.step === 'classSelection' ? 'process' : 'finish'}
                        title={t('moduleDetails:addModule.classSelection.title')}
                    />
                    <Steps.Step
                        status={state.step === 'settings' ? 'process' : 'wait'}
                        title={t('moduleDetails:addModule.configuration.title')}
                    />
                </Steps>
            </div>
        ),
        [current, state.step, stateDescription, t]
    );

    const body = (() => {
        switch (state.step) {
            case 'companySelection':
                return <ModuleCompanySelection onSelect={actions.goToClassSelection} />;

            case 'classSelection':
                return (
                    <ModuleClassSelection
                        company={state.company}
                        goBack={actions.goBackToCompanySelection}
                        onSelect={actions.goToSettings}
                    />
                );

            case 'settings':
                return (
                    <ModuleClassConfiguration
                        company={state.company}
                        goBack={actions.goBackToClassSelection}
                        moduleClass={state.selectedClass}
                    />
                );

            default:
                throw new Error('Step not implemented');
        }
    })();

    return (
        <ConsolePageWithHeader
            content={content}
            onBack={() => navigate('/admin/system/modules')}
            subTitle={t('moduleDetails:addModule.subTitle')}
            title={t('moduleDetails:addModule.title')}
        >
            {body}
        </ConsolePageWithHeader>
    );
};

export default AddModulePage;
